/**
 * Менеджер транзакций для CSElement
 * Управляет жизненным циклом транзакций и координирует операции
 */

import { EventEmitter } from 'eventemitter3';
import {
  TransactionManager,
  Transaction,
  TransactionConfig,
  TransactionResult,
  TransactionStatus,
  TransactionOperation,
  TransactionOperationType,
  IsolationLevel,
  Savepoint,
  TransactionInfo,
  TransactionEventType,
  TransactionEventData,
  LockManager
} from '../types/transaction-interfaces';
import { LockManagerImpl } from './LockManagerImpl';

/**
 * Простая реализация транзакции для менеджера
 */
class SimpleTransaction extends EventEmitter implements Transaction {
  private _id: string;
  private _status: TransactionStatus;
  private _config: TransactionConfig;
  private _startTime: number;
  private _endTime?: number;
  private _operations: TransactionOperation[] = [];
  private _savepoints: Savepoint[] = [];
  private _errors: string[] = [];
  private _lockManager?: LockManager;

  constructor(id: string, config: TransactionConfig, lockManager?: LockManager) {
    super();
    this._id = id;
    this._status = TransactionStatus.ACTIVE;
    this._config = config;
    this._startTime = Date.now();
    this._lockManager = lockManager;

    // Устанавливаем таймаут если указан
    if (config.timeout) {
      setTimeout(() => {
        if (this._status === TransactionStatus.ACTIVE) {
          this.rollback();
        }
      }, config.timeout);
    }
  }

  get id(): string {
    return this._id;
  }

  get status(): TransactionStatus {
    return this._status;
  }

  get startTime(): number {
    return this._startTime;
  }

  get config(): TransactionConfig {
    return { ...this._config };
  }

  async addOperation(operation: TransactionOperation): Promise<TransactionResult> {
    if (this._status !== TransactionStatus.ACTIVE) {
      return {
        success: false,
        error: `Транзакция не активна (статус: ${this._status})`,
        timestamp: Date.now()
      };
    }

    try {
      // Проверяем лимит операций
      if (this._config.maxOperations && this._operations.length >= this._config.maxOperations) {
        throw new Error(`Превышен лимит операций: ${this._config.maxOperations}`);
      }

      // Получаем блокировку если нужно
      if (this._lockManager) {
        const lockType = this.getLockTypeForOperation(operation.type);
        const lockAcquired = await this._lockManager.acquireLock(
          operation.elementId, 
          this._id, 
          lockType
        );
        
        if (!lockAcquired) {
          throw new Error(`Не удалось получить блокировку для элемента ${operation.elementId}`);
        }
      }

      // Выполняем операцию
      const result = await operation.execute();

      // Добавляем в список операций
      this._operations.push(operation);

      this.emitEvent({
        type: TransactionEventType.OPERATION_EXECUTED,
        timestamp: Date.now(),
        transactionId: this._id,
        operationId: operation.id,
        elementId: operation.elementId
      });

      return {
        success: true,
        data: result,
        operationId: operation.id,
        timestamp: Date.now()
      };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      this._errors.push(errorMessage);

      this.emitEvent({
        type: TransactionEventType.OPERATION_FAILED,
        timestamp: Date.now(),
        transactionId: this._id,
        operationId: operation.id,
        elementId: operation.elementId,
        error: errorMessage
      });

      return {
        success: false,
        error: errorMessage,
        operationId: operation.id,
        timestamp: Date.now()
      };
    }
  }

  async createSavepoint(name: string): Promise<Savepoint> {
    if (this._status !== TransactionStatus.ACTIVE) {
      throw new Error('Можно создавать точки сохранения только в активной транзакции');
    }

    const savepoint: Savepoint = {
      id: `sp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name,
      transactionId: this._id,
      timestamp: Date.now(),
      operationIndex: this._operations.length,
      snapshots: [] // В упрощенной реализации
    };

    this._savepoints.push(savepoint);

    this.emitEvent({
      type: TransactionEventType.SAVEPOINT_CREATED,
      timestamp: Date.now(),
      transactionId: this._id,
      savepointId: savepoint.id
    });

    return savepoint;
  }

  async rollbackToSavepoint(savepointId: string): Promise<TransactionResult> {
    if (this._status !== TransactionStatus.ACTIVE) {
      return {
        success: false,
        error: 'Откат к точке сохранения возможен только в активной транзакции',
        timestamp: Date.now()
      };
    }

    const savepoint = this._savepoints.find(sp => sp.id === savepointId);
    if (!savepoint) {
      return {
        success: false,
        error: `Точка сохранения ${savepointId} не найдена`,
        timestamp: Date.now()
      };
    }

    try {
      // Откатываем операции после точки сохранения
      const operationsToRollback = this._operations.slice(savepoint.operationIndex);
      
      for (let i = operationsToRollback.length - 1; i >= 0; i--) {
        const operation = operationsToRollback[i];
        try {
          await operation.rollback();
        } catch (error) {
          console.error(`Ошибка отката операции ${operation.id}:`, error);
        }
      }

      // Удаляем откаченные операции
      this._operations = this._operations.slice(0, savepoint.operationIndex);

      this.emitEvent({
        type: TransactionEventType.SAVEPOINT_ROLLED_BACK,
        timestamp: Date.now(),
        transactionId: this._id,
        savepointId: savepoint.id
      });

      return {
        success: true,
        data: { rolledBackOperations: operationsToRollback.length },
        timestamp: Date.now()
      };

    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error),
        timestamp: Date.now()
      };
    }
  }

  async releaseSavepoint(savepointId: string): Promise<TransactionResult> {
    const index = this._savepoints.findIndex(sp => sp.id === savepointId);
    if (index === -1) {
      return {
        success: false,
        error: `Точка сохранения ${savepointId} не найдена`,
        timestamp: Date.now()
      };
    }

    this._savepoints.splice(index, 1);

    this.emitEvent({
      type: TransactionEventType.SAVEPOINT_RELEASED,
      timestamp: Date.now(),
      transactionId: this._id,
      savepointId
    });

    return {
      success: true,
      timestamp: Date.now()
    };
  }

  async commit(): Promise<TransactionResult> {
    if (this._status !== TransactionStatus.ACTIVE) {
      return {
        success: false,
        error: `Нельзя зафиксировать транзакцию в статусе ${this._status}`,
        timestamp: Date.now()
      };
    }

    try {
      this._status = TransactionStatus.COMMITTING;
      this._endTime = Date.now();

      this.emitEvent({
        type: TransactionEventType.TRANSACTION_COMMITTING,
        timestamp: Date.now(),
        transactionId: this._id
      });

      // В реальной реализации здесь была бы двухфазная фиксация
      this._status = TransactionStatus.COMMITTED;

      // Освобождаем блокировки
      if (this._lockManager) {
        await this._lockManager.releaseAllLocks(this._id);
      }

      this.emitEvent({
        type: TransactionEventType.TRANSACTION_COMMITTED,
        timestamp: Date.now(),
        transactionId: this._id
      });

      return {
        success: true,
        data: {
          operationsCount: this._operations.length,
          duration: this._endTime - this._startTime
        },
        timestamp: Date.now()
      };

    } catch (error) {
      this._status = TransactionStatus.FAILED;
      const errorMessage = error instanceof Error ? error.message : String(error);
      this._errors.push(errorMessage);

      this.emitEvent({
        type: TransactionEventType.TRANSACTION_FAILED,
        timestamp: Date.now(),
        transactionId: this._id,
        error: errorMessage
      });

      return {
        success: false,
        error: errorMessage,
        timestamp: Date.now()
      };
    }
  }

  async rollback(): Promise<TransactionResult> {
    if (this._status === TransactionStatus.COMMITTED) {
      return {
        success: false,
        error: 'Нельзя откатить зафиксированную транзакцию',
        timestamp: Date.now()
      };
    }

    if (this._status === TransactionStatus.ROLLED_BACK) {
      return {
        success: true,
        data: { message: 'Транзакция уже откачена' },
        timestamp: Date.now()
      };
    }

    try {
      this._status = TransactionStatus.ROLLING_BACK;
      this._endTime = Date.now();

      this.emitEvent({
        type: TransactionEventType.TRANSACTION_ROLLING_BACK,
        timestamp: Date.now(),
        transactionId: this._id
      });

      // Откатываем операции в обратном порядке
      for (let i = this._operations.length - 1; i >= 0; i--) {
        const operation = this._operations[i];
        try {
          await operation.rollback();
        } catch (error) {
          console.error(`Ошибка отката операции ${operation.id}:`, error);
        }
      }

      this._status = TransactionStatus.ROLLED_BACK;

      // Освобождаем блокировки
      if (this._lockManager) {
        await this._lockManager.releaseAllLocks(this._id);
      }

      this.emitEvent({
        type: TransactionEventType.TRANSACTION_ROLLED_BACK,
        timestamp: Date.now(),
        transactionId: this._id
      });

      return {
        success: true,
        data: {
          rolledBackOperations: this._operations.length,
          duration: this._endTime - this._startTime
        },
        timestamp: Date.now()
      };

    } catch (error) {
      this._status = TransactionStatus.FAILED;
      const errorMessage = error instanceof Error ? error.message : String(error);
      this._errors.push(errorMessage);

      this.emitEvent({
        type: TransactionEventType.TRANSACTION_FAILED,
        timestamp: Date.now(),
        transactionId: this._id,
        error: errorMessage
      });

      return {
        success: false,
        error: errorMessage,
        timestamp: Date.now()
      };
    }
  }

  getInfo(): TransactionInfo {
    return {
      id: this._id,
      status: this._status,
      startTime: this._startTime,
      endTime: this._endTime,
      duration: this._endTime ? this._endTime - this._startTime : Date.now() - this._startTime,
      operationsCount: this._operations.length,
      config: this._config,
      operations: {
        total: this._operations.length,
        committed: this._status === TransactionStatus.COMMITTED ? this._operations.length : 0,
        rolledBack: this._status === TransactionStatus.ROLLED_BACK ? this._operations.length : 0,
        failed: this._errors.length
      },
      locks: [], // В упрощенной реализации
      errors: [...this._errors]
    };
  }

  getOperations(): TransactionOperation[] {
    return [...this._operations];
  }

  getSavepoints(): Savepoint[] {
    return [...this._savepoints];
  }

  private getLockTypeForOperation(_operationType: TransactionOperationType): 'read' | 'write' {
    // Большинство операций требуют write-блокировки
    return 'write';
  }

  private emitEvent(eventData: TransactionEventData): void {
    this.emit('transaction:event', eventData);
    this.emit(eventData.type, eventData);
  }
}

/**
 * Реализация менеджера транзакций
 */
export class TransactionManagerImpl extends EventEmitter implements TransactionManager {
  private transactions: Map<string, Transaction> = new Map();
  private currentTransaction: Transaction | null = null;
  private lockManager: LockManager;
  private stats = {
    active: 0,
    committed: 0,
    rolledBack: 0,
    failed: 0,
    totalOperations: 0,
    totalDuration: 0
  };

  constructor(lockManager?: LockManager) {
    super();
    this.lockManager = lockManager || new LockManagerImpl();
  }

  async begin(config: TransactionConfig = {}): Promise<Transaction> {
    const transactionId = this.generateTransactionId();
    
    const defaultConfig: TransactionConfig = {
      isolationLevel: IsolationLevel.READ_COMMITTED,
      timeout: 30000,
      maxOperations: 1000,
      autoCommit: false,
      savepoints: true,
      lockTimeout: 10000,
      ...config
    };

    const transaction = new SimpleTransaction(transactionId, defaultConfig, this.lockManager);
    
    // Подписываемся на события транзакции
    transaction.on('transaction:event', (eventData: TransactionEventData) => {
      this.handleTransactionEvent(eventData);
    });

    this.transactions.set(transactionId, transaction);
    this.currentTransaction = transaction;
    this.stats.active++;

    this.emit('transaction:started', { transactionId, config: defaultConfig });

    return transaction;
  }

  getCurrentTransaction(): Transaction | null {
    return this.currentTransaction;
  }

  getTransaction(id: string): Transaction | null {
    return this.transactions.get(id) || null;
  }

  getActiveTransactions(): Transaction[] {
    const activeTransactions: Transaction[] = [];
    
    for (const transaction of this.transactions.values()) {
      if (transaction.status === TransactionStatus.ACTIVE) {
        activeTransactions.push(transaction);
      }
    }
    
    return activeTransactions;
  }

  async withTransaction<T>(
    callback: (transaction: Transaction) => Promise<T>,
    config?: TransactionConfig
  ): Promise<T> {
    const transaction = await this.begin(config);
    
    try {
      const result = await callback(transaction);
      
      if (config?.autoCommit !== false) {
        await transaction.commit();
      }
      
      return result;
      
    } catch (error) {
      await transaction.rollback();
      throw error;
    }
  }

  async forceComplete(transactionId: string, commit: boolean): Promise<TransactionResult> {
    const transaction = this.transactions.get(transactionId);
    
    if (!transaction) {
      return {
        success: false,
        error: `Транзакция ${transactionId} не найдена`,
        timestamp: Date.now()
      };
    }

    if (transaction.status !== TransactionStatus.ACTIVE) {
      return {
        success: false,
        error: `Транзакция ${transactionId} не активна`,
        timestamp: Date.now()
      };
    }

    return commit ? transaction.commit() : transaction.rollback();
  }

  async cleanup(olderThan: number = 3600000): Promise<number> { // по умолчанию 1 час
    const cutoffTime = Date.now() - olderThan;
    let cleanedCount = 0;

    for (const [id, transaction] of this.transactions) {
      const info = transaction.getInfo();
      
      if (info.endTime && info.endTime < cutoffTime && 
          (transaction.status === TransactionStatus.COMMITTED || 
           transaction.status === TransactionStatus.ROLLED_BACK ||
           transaction.status === TransactionStatus.FAILED)) {
        
        this.transactions.delete(id);
        cleanedCount++;
        
        if (this.currentTransaction?.id === id) {
          this.currentTransaction = null;
        }
      }
    }

    this.emit('transactions:cleaned', { count: cleanedCount, olderThan });
    
    return cleanedCount;
  }

  getStats() {
    const averageDuration = this.stats.committed > 0 ? 
      this.stats.totalDuration / this.stats.committed : 0;
    
    return { 
      ...this.stats,
      averageDuration
    };
  }

  private handleTransactionEvent(eventData: TransactionEventData): void {
    // Обновляем статистику
    switch (eventData.type) {
      case TransactionEventType.TRANSACTION_COMMITTED:
        this.stats.active--;
        this.stats.committed++;
        if (eventData.metadata?.duration) {
          this.stats.totalDuration += eventData.metadata.duration;
        }
        break;
        
      case TransactionEventType.TRANSACTION_ROLLED_BACK:
        this.stats.active--;
        this.stats.rolledBack++;
        if (eventData.metadata?.duration) {
          this.stats.totalDuration += eventData.metadata.duration;
        }
        break;
        
      case TransactionEventType.TRANSACTION_FAILED:
        this.stats.active--;
        this.stats.failed++;
        break;
        
      case TransactionEventType.OPERATION_EXECUTED:
        this.stats.totalOperations++;
        break;
    }

    // Пересылаем событие
    this.emit('transaction:event', eventData);
    this.emit(eventData.type, eventData);
  }

  private generateTransactionId(): string {
    return `tx_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Получение менеджера блокировок
   */
  getLockManager(): LockManager {
    return this.lockManager;
  }

  /**
   * Очистка ресурсов
   */
  destroy(): void {
    // Завершаем все активные транзакции
    for (const transaction of this.getActiveTransactions()) {
      transaction.rollback().catch(console.error);
    }

    this.transactions.clear();
    this.currentTransaction = null;
    
    if (this.lockManager && 'destroy' in this.lockManager) {
      (this.lockManager as any).destroy();
    }
    
    this.removeAllListeners();
  }
} 